/**
 * Тема оформления: светлая, тёмная или как в системе.
 *
 * Светлая — та, что была всегда, и она остаётся умолчанием: тёмная добавлена
 * второй, по образцу KEPwise, а не вместо. Выбор человека живёт в браузере,
 * как язык; «как в системе» следует за настройкой телефона и меняется
 * вместе с ней.
 *
 * Счёт вынесен в чистую функцию по той же причине, что и язык: хранилища и
 * системы в тестах нет, а ошибка здесь видна глазом сразу — не тот цвет на
 * весь экран.
 */

export const THEMES = ['light', 'dark', 'system'] as const
export type Theme = (typeof THEMES)[number]

/** То, что реально видно на экране: система уже разрешена в одно из двух. */
export type Look = 'light' | 'dark'

const STORAGE_KEY = 'telematic.theme'

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

/**
 * Какой вид показывать. Незнакомое или пустое сохранённое значение — светлая:
 * тёмная тема второстепенна, и подставлять её по ошибке нельзя.
 */
export function resolveLook(saved: unknown, systemDark: boolean): Look {
  if (saved === 'dark') return 'dark'
  if (saved === 'system') return systemDark ? 'dark' : 'light'
  return 'light'
}

export function savedTheme(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isTheme(raw) ? raw : 'light'
  } catch {
    return 'light' // приватный режим или закрытое хранилище — светлая
  }
}

export function rememberTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // не сохранилось — выбор проживёт до конца сессии
  }
}

function systemDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Применить тему к странице: атрибут на корне и цвет полосы браузера.
 *
 * Атрибут, а не класс: по нему стили переключают переменные, а `theme-color`
 * красит собственную полосу Safari под страницей — без него под тёмной
 * шторкой оставалась бы светлая полоска, и шторка выглядела бы висящей.
 */
export function applyTheme(theme: Theme): Look {
  const look = resolveLook(theme, systemDark())
  document.documentElement.dataset.theme = look
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', look === 'dark' ? '#05070c' : '#F0F0F3')
  return look
}

/** Следить за системной темой, пока выбрано «как в системе». */
export function watchSystem(onChange: () => void): () => void {
  if (typeof matchMedia !== 'function') return () => {}
  const query = matchMedia('(prefers-color-scheme: dark)')
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
